/**
 * SEO Utilities for generating dynamic meta tags and social sharing content
 */

// Helper function to get optimized image URL with sizing
export const getOptimizedImageUrl = (imageUrl, width = 1200, height = 630) => {
  if (!imageUrl) return null;

  // If it's already a full URL, return as is
  if (imageUrl.startsWith('http')) {
    return imageUrl;
  }

  // Add sizing parameters for optimization
  const separator = imageUrl.includes('?') ? '&' : '?';
  return `${imageUrl}${separator}width=${width}&height=${height}`;
};

// Extract poster image from various content types
export const extractPosterImage = (content, contentType) => {
  let posterUrl = null;

  switch (contentType) {
    case 'movie':
      // Try movie poster, then channel poster, then thumbnail
      posterUrl =
        content.poster || content.channel?.poster || content.channel?.thumb || content.thumb;
      break;

    case 'series':
      // Try series poster, then channel poster, then thumbnail
      posterUrl =
        content.poster || content.channel?.poster || content.channel?.thumb || content.thumb;
      break;

    case 'channel':
      // Try channel poster, then thumbnail
      posterUrl = content.poster || content.thumb || content.channel_logo;
      break;

    case 'episode':
      // Try episode thumb, then series poster, then channel poster
      posterUrl =
        content.thumb ||
        content.series?.poster ||
        content.channel?.poster ||
        content.channel?.thumb;
      break;

    default:
      posterUrl = content.poster || content.thumb;
  }

  return getOptimizedImageUrl(posterUrl, 1200, 630);
};

// Generate SEO data for movies
export const generateMovieSEO = (movie, channelData = null) => {
  const title = `Watch ${movie.title || movie.name} - Free Streaming on ChillFree TV`;
  const description =
    movie.description ||
    `Stream ${
      movie.title || movie.name
    } for free on ChillFree TV. Enjoy classic movies and timeless entertainment without subscription fees.`;

  const posterImage =
    extractPosterImage(movie, 'movie') || extractPosterImage(channelData, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'video.movie',
    url: `/movie/${movie.slug || movie._id}`,
  };
};

// Generate SEO data for series
export const generateSeriesSEO = (series, channelData = null) => {
  const title = `Stream ${series.title || series.name} Episodes - ChillFree TV`;
  const description =
    series.description ||
    `Watch all episodes of ${
      series.title || series.name
    } for free on ChillFree TV. Classic TV series and timeless shows available now.`;

  const posterImage =
    extractPosterImage(series, 'series') || extractPosterImage(channelData, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'video.tv_show',
    url: `/series/${series.slug || series._id}`,
  };
};

// Generate SEO data for episodes
export const generateEpisodeSEO = (episode, series, channelData = null) => {
  const episodeTitle = episode.title || `Episode ${episode.episode_number || ''}`;
  const seriesTitle = series?.title || series?.name || 'Series';

  const title = `${episodeTitle} - ${seriesTitle} | ChillFree TV`;
  const description =
    episode.description ||
    `Watch ${episodeTitle} from ${seriesTitle} for free on ChillFree TV. Stream classic TV episodes without subscription fees.`;

  const posterImage =
    extractPosterImage(episode, 'episode') ||
    extractPosterImage(series, 'series') ||
    extractPosterImage(channelData, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'video.episode',
    url: `/watch/${channelData?.slug || channelData?._id}/${episode._id}`,
  };
};

// Generate SEO data for channels/live TV
export const generateChannelSEO = channel => {
  const title = `${channel.title || channel.name} - Live TV Streaming | ChillFree TV`;
  const description =
    channel.description ||
    `Watch ${
      channel.title || channel.name
    } live on ChillFree TV. Free streaming with classic content and timeless entertainment.`;

  const posterImage = extractPosterImage(channel, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'website',
    url: `/live-tv/${channel.slug || channel._id}`,
  };
};

// Generate SEO data for static pages
export const generatePageSEO = (pageType, customData = {}) => {
  const seoData = {
    homepage: {
      title: 'ChillFree TV - Free Streaming Movies & Classic TV Shows',
      description:
        'Stream classic movies and timeless TV shows for free on ChillFree TV. No subscription required. Enjoy curated entertainment from the golden age of cinema and television.',
      url: '/',
    },
    about: {
      title: 'About ChillFree TV - Free Streaming Platform Built with AI',
      description:
        'Learn about ChillFree TV, the free streaming platform built with cutting-edge AI technology. Discover our mission to bring classic entertainment to everyone.',
      url: '/about',
    },
    contact: {
      title: 'Contact ChillFree TV - Support & Customer Service',
      description:
        'Get in touch with ChillFree TV support team. Contact us for technical help, content requests, or general inquiries about our free streaming service.',
      url: '/contact',
    },
    privacy: {
      title: 'Privacy Policy - ChillFree TV Data Protection & CCPA Compliance',
      description:
        'Read ChillFree TV privacy policy. Learn how we protect your data, our CCPA compliance, and your privacy rights on our free streaming platform.',
      url: '/privacy-policy',
    },
    terms: {
      title: 'Terms of Use - ChillFree TV Legal Terms & Conditions',
      description:
        'Review ChillFree TV terms of use and legal conditions for using our free streaming service. Understand your rights and responsibilities.',
      url: '/terms-of-use',
    },
    ccpa: {
      title: 'CCPA Rights - California Privacy Rights | ChillFree TV',
      description:
        'Exercise your California Consumer Privacy Act (CCPA) rights on ChillFree TV. Learn about data protection and privacy controls available to you.',
      url: '/ccpa',
    },
    'do-not-sell': {
      title: 'Do Not Sell My Data - CCPA Opt-Out | ChillFree TV',
      description:
        'Opt out of personal data sales under CCPA. Submit your "Do Not Sell My Data" request to ChillFree TV for enhanced privacy protection.',
      url: '/do-not-sell',
    },
    'live-tv': {
      title: 'Live TV Streaming - Free Channels | ChillFree TV',
      description:
        'Watch live TV channels for free on ChillFree TV. Stream classic content, movies, and shows with our curated live television experience.',
      url: '/live-tv',
    },
    movies: {
      title: 'Free Movies - Classic Cinema Streaming | ChillFree TV',
      description:
        'Discover free classic movies on ChillFree TV. Stream timeless cinema from the golden age of Hollywood without subscription fees.',
      url: '/movies',
    },
    series: {
      title: 'Free TV Series - Classic Shows Streaming | ChillFree TV',
      description:
        'Watch free classic TV series on ChillFree TV. Stream complete seasons of timeless television shows from the golden age of TV.',
      url: '/series',
    },
  };

  const defaultData = seoData[pageType] || seoData.homepage;

  return {
    ...defaultData,
    ...customData,
    type: 'website',
    image: customData.image || null, // Will use default ChillFree image
  };
};

// Generate structured data (JSON-LD) for search engines
export const generateStructuredData = (content, contentType) => {
  const baseData = {
    '@context': 'https://schema.org',
    publisher: {
      '@type': 'Organization',
      name: 'ChillFree TV',
      url: 'https://chillfree.tv',
      logo: {
        '@type': 'ImageObject',
        url: 'https://chillfree.tv/assets/images/chillfree-logo.png',
      },
    },
  };

  switch (contentType) {
    case 'movie':
      return {
        ...baseData,
        '@type': 'Movie',
        name: content.title || content.name,
        description: content.description,
        image: extractPosterImage(content, 'movie'),
        url: `https://chillfree.tv/movie/${content.slug || content._id}`,
        datePublished: content.created_at,
        genre: content.genre || 'Classic',
        duration: content.duration ? `PT${content.duration}M` : undefined,
      };

    case 'series':
      return {
        ...baseData,
        '@type': 'TVSeries',
        name: content.title || content.name,
        description: content.description,
        image: extractPosterImage(content, 'series'),
        url: `https://chillfree.tv/series/${content.slug || content._id}`,
        datePublished: content.created_at,
        genre: content.genre || 'Classic',
        numberOfEpisodes: content.playlist?.length,
      };

    case 'episode':
      return {
        ...baseData,
        '@type': 'TVEpisode',
        name: content.title,
        description: content.description,
        image: extractPosterImage(content, 'episode'),
        episodeNumber: content.episode_number,
        seasonNumber: content.season_number,
        partOfSeries: {
          '@type': 'TVSeries',
          name: content.series?.title || content.series?.name,
        },
      };

    default:
      return {
        ...baseData,
        '@type': 'WebSite',
        name: 'ChillFree TV',
        url: 'https://chillfree.tv',
        description: 'Free streaming platform for classic movies and TV shows',
      };
  }
};
